import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { subDays, format } from 'date-fns';

// GraphQL query to get the count of unviewed live domains
// Using distinct_on to count domains as they appear in the UI
const GET_LIVE_DOMAIN_COUNT = gql`
  query GetLiveDomainCount($startDate: timestamp!, $endDate: timestamp!) {
    live_domains_aggregate(
      where: {
        viewed_at: {_is_null: true},
        times: {_gte: $startDate, _lte: $endDate}
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const LiveDomainCountContext = createContext(null);

export function LiveDomainCountProvider({ children }) {
  const [count, setCount] = useState(0);
  
  // Add useMemo for dates to prevent unnecessary recalculations
  const startDate = useMemo(() => 
    format(subDays(new Date(), 30), 'yyyy-MM-dd'),
  []); // Should be memoized with empty array

  const endDate = useMemo(() => 
    format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
  []); // Memoize with empty array unless you need dynamic updates

  // Query for live domain count
  const { data, loading, refetch } = useQuery(GET_LIVE_DOMAIN_COUNT, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: 600000,
    notifyOnNetworkStatusChange: false,
    onError: (error) => {
      console.error('Error fetching live domain count:', error);
    }
  });

  // Set count when data is updated
  useEffect(() => {
    if (data && data.live_domains) {
      setCount(data.live_domains.length || 0);
    }
  }, [data]);

  // Use useMemo to memoize the context value
  const contextValue = useMemo(() => ({ 
    count, 
    setCount, 
    loading, 
    refetch 
  }), [count, loading, refetch]);

  return (
    <LiveDomainCountContext.Provider value={contextValue}>
      {children}
    </LiveDomainCountContext.Provider>
  );
}

LiveDomainCountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useLiveDomainCount() {
  const context = useContext(LiveDomainCountContext);
  if (!context) {
    throw new Error('useLiveDomainCount must be used within a LiveDomainCountProvider');
  }
  return context;
} 